// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { AlertCircle } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { CSV_IMPORT_TYPES, CsvImportError, type CsvImportType, useImportCsvMutation } from "@/api/hooks/use-imports";
import { ImportPlusIcon } from "@/components/icons";
import { FileDropZone } from "@/components/shared/file-drop-zone";
import { KeyChip } from "@/components/shared/key-chip";
import { WizardPane } from "@/components/shared/wizard-pane";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";

/** How many backend error entries are listed before the rest is collapsed into a counter. */
const MAX_LISTED_ERRORS = 20;

interface ImportCsvDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  storeName: string;
}

/**
 * Uploads a single CSV file for one store, bypassing transformations: the chosen import type decides
 * which entity the rows are converted into on the backend (`POST /v1/ingress/csv`).
 */
export function ImportCsvDialog({ open, onOpenChange, storeName }: ImportCsvDialogProps) {
  const { t } = useTranslation("back-office");
  const importCsv = useImportCsvMutation();

  const [importType, setImportType] = useState<CsvImportType | "">("");
  const [file, setFile] = useState<File | null>(null);
  const [overrideStoreCode, setOverrideStoreCode] = useState(false);
  const [progress, setProgress] = useState<number | undefined>(undefined);

  // Stable across renders, unlike the mutation result object it hangs off.
  const resetMutation = importCsv.reset;

  useEffect(
    function resetWhenClosed() {
      if (open) return;
      setImportType("");
      setFile(null);
      setOverrideStoreCode(false);
      setProgress(undefined);
      resetMutation();
    },
    [open, resetMutation],
  );

  const errorEntries = importCsv.error instanceof CsvImportError ? importCsv.error.entries : [];

  async function onUpload() {
    if (!importType || !file) return;
    setProgress(0);
    try {
      const result = await importCsv.mutateAsync({
        storeName,
        importType,
        file,
        overrideStoreCode,
        onProgress: setProgress,
      });
      toast.success(t("imports.csv.success", { id: result.importRequestId, count: result.itemsFound }));
      onOpenChange(false);
    } catch {
      // Rendered inline — the backend lists the offending rows and columns.
    } finally {
      setProgress(undefined);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        // Closing mid-upload would strand the request; the file is already on its way.
        if (!o && importCsv.isPending) return;
        onOpenChange(o);
      }}
    >
      <DialogContent
        id="dialog-import-csv"
        className="sm:max-w-xl p-0 gap-0 overflow-hidden flex flex-col"
        aria-describedby={undefined}
        showCloseButton={!importCsv.isPending}
      >
        <div className="flex flex-row">
          <WizardPane icon={ImportPlusIcon} />
          <div className="flex-1 flex flex-col min-w-0 min-h-0">
            <DialogHeader className="p-6 pb-0">
              <DialogTitle>{t("imports.csv.title")}</DialogTitle>
              <div className="flex items-center gap-2 pt-1">
                <KeyChip label={storeName} variant="store" />
              </div>
            </DialogHeader>

            <div className="flex flex-col gap-4 overflow-y-auto max-h-[60vh] p-6 pt-4">
              <div className="space-y-1.5">
                <Label htmlFor="csv-import-type">{t("imports.csv.typeLabel")}</Label>
                <Select
                  value={importType}
                  onValueChange={(value) => {
                    setImportType(value as CsvImportType);
                    // A file only makes sense for the type it was picked for — drop it on a switch.
                    setFile(null);
                    importCsv.reset();
                  }}
                >
                  <SelectTrigger id="csv-import-type" className="w-full" disabled={importCsv.isPending}>
                    <SelectValue placeholder={t("imports.csv.typePlaceholder")} />
                  </SelectTrigger>
                  <SelectContent>
                    {CSV_IMPORT_TYPES.map((type) => (
                      <SelectItem key={type} value={type}>
                        {t(`imports.csv.types.${type}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Everything below only makes sense for a chosen type — it defines what the columns
                  must be, and whether the file's store code even matters. */}
              {importType && (
                <>
                  <div className="space-y-1.5">
                    <Label>{t("imports.csv.fileLabel")}</Label>
                    <FileDropZone
                      accept=".csv"
                      selected={file && progress === undefined ? { name: file.name, size: file.size } : undefined}
                      progress={progress}
                      title={t("imports.csv.fileDropTitle")}
                      uploadingLabel={(percent) => t("imports.csv.uploadingPercent", { percent })}
                      processingLabel={t("imports.csv.processing")}
                      disabled={importCsv.isPending}
                      onSelect={(selected) => {
                        setFile(selected);
                        importCsv.reset();
                      }}
                    />
                  </div>

                  {/* Store code override — files exported by the customer often carry their own store
                      code; replacing it beats asking them to re-export. */}
                  <div className="flex items-center gap-3">
                    <Switch
                      id="csv-override-store-code"
                      checked={overrideStoreCode}
                      onCheckedChange={(checked) => {
                        setOverrideStoreCode(checked);
                        importCsv.reset();
                      }}
                      disabled={importCsv.isPending}
                    />
                    <Label htmlFor="csv-override-store-code">{t("imports.csv.overrideStoreCode")}</Label>
                  </div>
                </>
              )}

              {importCsv.isError && (
                <div className="rounded-lg border border-destructive bg-destructive/10 p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertCircle className="h-5 w-5 text-destructive" />
                    <span className="font-medium text-destructive">{t("imports.csv.errorTitle")}</span>
                  </div>
                  {errorEntries.length > 0 ? (
                    <ul className="space-y-0.5">
                      {errorEntries.slice(0, MAX_LISTED_ERRORS).map((entry, i) => (
                        <li key={i} className="text-sm text-muted-foreground">
                          <span className="font-mono text-xs">{entry.detail}</span>
                          {entry.msg && <span> — {entry.msg}</span>}
                        </li>
                      ))}
                      {errorEntries.length > MAX_LISTED_ERRORS && (
                        <li className="text-sm text-muted-foreground">
                          {t("imports.csv.moreErrors", { count: errorEntries.length - MAX_LISTED_ERRORS })}
                        </li>
                      )}
                    </ul>
                  ) : (
                    <p className="text-sm text-muted-foreground">{importCsv.error?.message}</p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        <DialogFooter className="mx-0 mb-0 rounded-none rounded-b-lg">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={importCsv.isPending}>
            {t("buttons.cancel")}
          </Button>
          <Button onClick={onUpload} disabled={!importType || !file || importCsv.isPending}>
            <ImportPlusIcon className="h-4 w-4" />
            {t("imports.csv.upload")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
